import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import { downloadFile } from '@/components/UI/Table/Reusable/APITable/APITable.utils';
import { successToast } from '@/helpers';
import { _notNil } from '@/littledash';
import type { FC } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { createSelector } from '@reduxjs/toolkit';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useAsyncExport } from '@/support/Hooks/api/useAsyncExport';
import { useAbortController } from '@/support/Hooks/fetch/useAbortController';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { featuresSelector } from '@/support/Selectors';
import type { DataTableErrors, DataTableService } from './DataTable.model';
import { DataTableToolbar } from './toolbar/DataTableToolbar';

const featureSelector = createSelector([featuresSelector], (features) => ({
  asyncExportsEnabled: features.async_exports === true,
}));

interface DataTableHeaderProps {
  dataTableService: DataTableService;
  dataTableErrors: DataTableErrors;
}

const DataTableHeader: FC<DataTableHeaderProps> = ({ dataTableService, dataTableErrors }) => {
  const { newAbortController } = useAbortController();
  const isMounted = useMountedState();
  const { id: studyId, dataTableId } = useParams<{ id: string; dataTableId: string }>();
  const { asyncExportsEnabled } = useSelector(featureSelector);
  const triggerAsyncExport = useAsyncExport();

  const { invoke: createDatatableExport, loading: queueingExport } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/exports/datatable',
    options: { invokeOnInit: false, onError: { toast: false, throw: false, slug: 'datatable-async-export' } },
  });

  const handleAsyncExportDataTable = async (): Promise<void> => {
    const studyApiId = dataTableService.studyApiId;
    const dataTableApiId = dataTableService.tableId;
    if (!_notNil(studyApiId) || !_notNil(dataTableApiId)) {
      return;
    }
    await triggerAsyncExport(() =>
      createDatatableExport({
        path: { studyApiId },
        body: { data_table_api_id: dataTableApiId },
      })
    );
  };

  const handleLegacyExportDataTable = async () => {
    try {
      const { data, headers } = await Http.get<Record<string, any>>(
        apiRoute('datatables.export', { studyId, dataTableId }),
        { signal: newAbortController().signal, responseType: 'blob' }
      );

      if (isMounted()) {
        downloadFile({ data, headers });
        successToast(`Downloaded ${headers['file-name']}`);
      }
    } catch (error: any) {
      // intentionally empty
    }
  };

  const handleExportDataTable = asyncExportsEnabled ? handleAsyncExportDataTable : handleLegacyExportDataTable;

  return (
    <div data-test-component="DataTableHeader" data-test-element="container">
      <div className="flex justify-between items-center ph3 pv2 bb b--moon-gray bg-white ">
        <h3 className="f6 lh-title near-black fw5" data-test-component="DataTableHeader" data-test-element="name">
          {dataTableService.name}
        </h3>
        <Button onClick={handleExportDataTable} disabled={queueingExport}>
          Export
        </Button>
      </div>
      {dataTableService.readonly && (
        <Banner className="mw6 ma3" info>
          <h3 className="f5 lh-title">You don't have access to edit this Datatable.</h3>
          <p className="f6 lh-title measure mt2">Request 'Write' access from the Study author or owner. </p>
        </Banner>
      )}
      <div className="bg-white">
        <DataTableToolbar dataTableService={dataTableService} errors={dataTableErrors} />
      </div>
    </div>
  );
};

export default DataTableHeader;
